// @ts-nocheck
import React, { useCallback } from 'react';
import { FormControl, Button, Box, Stack } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import PropTypes from 'prop-types';
import { isEmpty, isArray } from 'lodash';
import { ReactMuiTableColumnHeaderTextEllipsis } from '@nuva-org/solytics-frontend';
import mime from 'mime';

import {
  convertCsvToJson,
  createDynamicColumns,
} from '../../../../utils/Utils';
import ReactMuiTableListView from '../../ReactMuiTableListViewV8';
import swalToast from '../../swalToast';

/**
 * Table field component for uploading and displaying tabular data (CSV)
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {Array|null} props.value - Current field value (array of objects)
 * @param {Function} props.onChange - Function to handle value changes
 * @returns {React.ReactElement} The Table Field component
 */
const TableFieldComponent = ({ value, onChange }) => {
  /**
   * Handles the change event for a CSV file input.
   * @function
   * @param {Event} e - The change event object triggered by the file input
   * @returns {void}
   */
  const handleCsvChange = useCallback(
    (e) => {
      const file = e.target.files[0];

      let inputValue = e.target;
      if (inputValue) {
        inputValue.value = null;
      }

      // https://stackoverflow.com/questions/51724649/mime-type-of-file-returning-empty-in-javascript-on-some-machines
      const fType = mime.getType(file?.name);

      const csvFileType = 'text/csv';

      const isFileTypeCSV = [csvFileType].includes(file?.type)
        ? true
        : fType === csvFileType;

      if (file && isFileTypeCSV) {
        convertCsvToJson(file)
          .then((jsonArray) => {
            // we are sending columns in array as we want  to preserve the order of the columns
            const tableData = {
              columns: Object.keys(
                isArray(jsonArray) && jsonArray.length > 0 ? jsonArray[0] : []
              ),
              data: jsonArray,
            };
            onChange(tableData);
          })
          .catch((error) => {
            if (error) {
              swalToast({
                icon: 'error',
                title: `CSV parsing error: ${error?.message}`,
              });
            }
          });
      } else {
        swalToast({
          icon: 'error',
          title: 'File format not supported, please select CSV.',
        });
      }
    },
    [onChange, convertCsvToJson, swalToast]
  );

  /**
   * Handles removing the uploaded file
   * @function
   * @returns {void}
   */
  const handleRemoveFile = useCallback(() => {
    onChange(null);
  }, [onChange]);

  /**
   * Creates props for table rows
   * @function
   * @returns {Object} Row props
   */
  const handleGetTableRowProps = useCallback(
    () => ({
      style: {
        position: 'relative',
      },
    }),
    []
  );

  /**
   * Creates props for table columns
   * @function
   * @returns {Object} Column props
   */
  const handleGetTableColumnProps = useCallback(
    () => ({
      style: {
        textAlign: 'left',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'initial',
        whiteSpace: 'nowrap',
        paddingLeft: 10,
      },
    }),
    []
  );

  /**
   * Creates props for table headers
   * @function
   * @returns {Object} Header props
   */
  const handleGetHeaderProps = useCallback(
    () => ({
      style: {
        display: 'flex',
        alignItems: 'center',
      },
    }),
    []
  );

  // Create dynamic columns based on data structure
  const columns =
    value && value?.columns?.length > 0
      ? createDynamicColumns({
          data: value?.columns,
          width: 150,
          HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
        })
      : [];

  const tableData = value?.data || [];

  return (
    <FormControl fullWidth>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button
          variant="contained"
          component="label"
          title={tableData?.length ? 'Change CSV' : 'Upload CSV'}
          size="small"
          sx={{
            fontSize: '11px',
            height: 1,
            minWidth: 2,
            whiteSpace: 'nowrap',
          }}
        >
          {tableData?.length ? 'Change CSV' : 'Upload CSV'}
          <input
            type="file"
            accept=".csv"
            onChange={handleCsvChange}
            style={{ display: 'none' }}
          />
        </Button>
      </Stack>

      {tableData?.length > 0 && (
        <Box mt={2} display="flex" flexDirection="column">
          <Box mb={1}>
            <Button
              title="Remove file"
              color="primary"
              variant="outlined"
              onClick={handleRemoveFile}
              size="small"
              sx={{
                fontSize: '12px',
                height: 1,
                minWidth: 2,
                whiteSpace: 'nowrap',
              }}
              startIcon={<ClearIcon />}
            >
              Remove file
            </Button>
          </Box>
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: '300px',
            }}
          >
            <ReactMuiTableListView
              data={
                tableData?.length > 0 && isArray(tableData) ? tableData : []
              }
              columns={isEmpty(columns) ? [] : columns}
              getHeaderProps={handleGetHeaderProps}
              getRowProps={handleGetTableRowProps}
              getColumnProps={handleGetTableColumnProps}
              enableRowSelection={false}
              pageCount={tableData?.length}
              enablePagination={true}
              initialPageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              stickyHeader={true}
            />
          </Box>
        </Box>
      )}
    </FormControl>
  );
};

TableFieldComponent.propTypes = {
  value: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({}).isRequired,
};

export default TableFieldComponent;
